import { useEffect, useState } from "react";
import { geoServerBaseUrl } from "../services/geoServer";
import { geoServerAuthHeaders } from "../services/geoServerAuth";

export interface ColormapEntry {
  quantity: number;
  color: string;
  label: string;
  opacity: number;
}

const cache = new Map<string, ColormapEntry[]>();

function parseColormapEntries(data: any): ColormapEntry[] | null {
  const colormap =
    data?.Legend?.[0]?.rules?.[0]?.symbolizers?.[0]?.Raster?.colormap;

  // Só rampas contínuas: um colormap "values"/"intervals" descreve classes
  // (as máscaras 0/255 de pepe-cog, por exemplo), onde filtrar por faixa de
  // valor não quer dizer nada.
  if (
    !colormap ||
    colormap.type !== "ramp" ||
    !Array.isArray(colormap.entries)
  ) {
    return null;
  }

  const entries: ColormapEntry[] = colormap.entries
    .map((e: any) => ({
      quantity: Number(e.quantity),
      color: String(e.color),
      label: String(e.label ?? e.quantity),
      // Paradas transparentes (no-data, máscaras) precisam continuar
      // transparentes no SLD do recorte; ausente vale opaco.
      opacity: Number.isFinite(Number(e.opacity)) ? Number(e.opacity) : 1,
    }))
    .filter((e: ColormapEntry) => Number.isFinite(e.quantity) && !!e.color)
    .sort((a: ColormapEntry, b: ColormapEntry) => a.quantity - b.quantity);

  // Menos de duas paradas não descreve um gradiente contínuo — sem faixa de
  // valores não há o que filtrar preservando as cores.
  return entries.length >= 2 ? entries : null;
}

/**
 * Lê o colormap real do SLD do raster via GetLegendGraphic em JSON. É a fonte
 * das cores usadas para montar o SLD dinâmico do recorte e dos limites do
 * slider — sem isso teríamos de chutar a faixa de valores da camada.
 */
export function useRasterColormap(
  workspace: string | undefined,
  layerName: string | undefined
): ColormapEntry[] | null {
  const cacheKey = workspace && layerName ? `${workspace}:${layerName}` : null;
  // A chave viaja junto com as entradas: sem isso, no render em que a camada
  // muda o hook devolveria o colormap da camada anterior até o efeito rodar, e
  // quem consome os limites (o clamp do RasterRangeControl) gravaria limiares
  // da camada velha na camada nova.
  const [state, setState] = useState<{
    key: string | null;
    entries: ColormapEntry[] | null;
  }>(() => ({
    key: cacheKey,
    entries: cacheKey ? cache.get(cacheKey) ?? null : null,
  }));

  useEffect(() => {
    if (!cacheKey || !workspace || !layerName) {
      setState({ key: cacheKey, entries: null });
      return;
    }

    const cached = cache.get(cacheKey);
    if (cached) {
      setState({ key: cacheKey, entries: cached });
      return;
    }

    let cancelled = false;
    setState({ key: cacheKey, entries: null });

    const params = new URLSearchParams({
      service: "wms",
      version: "1.0.0",
      request: "GetLegendGraphic",
      format: "application/json",
      layer: layerName,
    });

    fetch(`${geoServerBaseUrl}/${workspace}/wms?${params}`, {
      headers: geoServerAuthHeaders(workspace),
    })
      .then((res) => res.json())
      .then((data) => {
        const parsed = parseColormapEntries(data);
        // Só sucesso vai para o cache: falha de rede cacheada deixaria a
        // camada sem cores até o reload da página.
        if (parsed) cache.set(cacheKey, parsed);
        if (!cancelled) setState({ key: cacheKey, entries: parsed });
      })
      .catch(() => {
        if (!cancelled) setState({ key: cacheKey, entries: null });
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, workspace, layerName]);

  return state.key === cacheKey ? state.entries : null;
}
